import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { Activity, ArrowLeft, ChevronLeft, ChevronRight, Loader2, RefreshCw, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loading } from "@/components/common";
import { toast } from "sonner";
const AdminContext = createContext({
  permissions: []
});
export const useAdminAccess = () => {
  const access = useContext(AdminContext);
  return {
    ...access,
    can: scope => access.permissions.includes(scope)
  };
};
export function useAdminData(url, interval = 0, enabled = true) {
  const sequence = useRef({
    value: 0
  });
  const [data, setData] = useState(null),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    if (!enabled) return;
    const current = ++sequence.current.value;
    try {
      const result = await api.get(url);
      if (sequence.current.value === current) {
        setData(result.data);
        setError("");
      }
    } catch (e) {
      if (sequence.current.value === current) setError(errMsg(e));
    } finally {
      if (sequence.current.value === current) setLoading(false);
    }
  }, [url, enabled]);
  useEffect(() => {
    const tracker = sequence.current;
    if (!enabled) { setData(null); setLoading(false); return; }
    setLoading(true);
    refresh();
    const timer = interval ? setInterval(refresh, interval) : null;
    return () => {
      tracker.value++;
      if (timer) clearInterval(timer);
    };
  }, [refresh, interval, enabled]);
  return {
    data,
    error,
    loading,
    refresh
  };
}
export function AdminConsole() {
  const {
    demo
  } = useAuth();
  const state = useAdminData("/admin/ops/access", 60000);
  const {
    pathname
  } = useLocation();
  if (state.loading) return <Loading label="Checking administration access" />;
  if (state.error) return <AdminError error={state.error} retry={state.refresh} />;
  const writeScope = {
    "/admin/payments": "billing.manage",
    "/admin/plans": "billing.manage",
    "/admin/providers": "ai.write",
    "/admin/routes": "ai.write",
    "/admin/usage": "usage.write",
    "/admin/sources": "sources.write"
  }[pathname];
  const readOnly = writeScope && !state.data.permissions.includes(writeScope);
  return <AdminContext.Provider value={state.data}><div className="admin-console"><div className="mb-6 flex flex-wrap items-center justify-between gap-3 border-b pb-4"><div className="flex items-center gap-2 text-xs"><ShieldCheck size={15} className="text-primary" /><span className="font-semibold">Administration</span><span className="rounded-full bg-primary/10 px-2 py-1 text-[10px] capitalize text-primary">{state.data.role?.replaceAll("_", " ")}</span><span className="hidden text-muted-foreground sm:inline">{demo ? "Local demo · MFA bypassed" : "MFA protected"}</span></div><Link to="/dashboard" className="flex items-center gap-1.5 text-xs text-muted-foreground"><ArrowLeft size={13} />Personal workspace</Link></div>{readOnly && <p className="mb-4 rounded-lg border bg-muted p-3 text-xs">Your role has read-only access to this section. Changes require a higher permission.</p>}<fieldset disabled={readOnly || undefined} className="min-w-0"><Outlet /></fieldset></div></AdminContext.Provider>;
}
export function AdminHeader({
  title,
  description,
  icon: Icon = Activity,
  refresh,
  children,
  testId
}) {
  return <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-start"><div><div className="mb-3 grid h-10 w-10 place-items-center rounded-xl border bg-card text-primary"><Icon size={21} /></div><h1 className="text-2xl font-bold tracking-tight sm:text-3xl" data-testid={testId}>{title}</h1><p className="mt-2 max-w-2xl text-xs leading-relaxed text-muted-foreground">{description}</p></div><div className="flex flex-wrap gap-2">{refresh && <Button variant="outline" size="sm" onClick={refresh}><RefreshCw size={14} />Refresh</Button>}{children}</div></div>;
}
export function AdminError({
  error,
  retry
}) {
  return <div role="alert" className="panel mb-5 space-y-3 p-5"><p className="text-sm text-destructive">{error}</p>{retry && <Button variant="outline" size="sm" onClick={retry}>Try again</Button>}</div>;
}
export function StatGrid({
  items
}) {
  return <div className="mb-6 grid grid-cols-2 gap-3 xl:grid-cols-4">{items.map(({
      label,
      value,
      detail,
      icon: Icon
    }) => <div key={label} className="panel p-4 sm:p-5"><div className="flex items-center justify-between gap-2"><p className="text-[11px] text-muted-foreground">{label}</p>{Icon && <Icon size={16} className="text-primary" />}</div><p className="mt-3 font-display text-2xl font-bold">{value ?? "—"}</p>{detail && <p className="mt-2 text-[10px] leading-relaxed text-muted-foreground">{detail}</p>}</div>)}</div>;
}
export function Pager({
  data,
  page,
  setPage
}) {
  if (!data) return null;
  return <div className="flex items-center justify-between border-t px-4 py-3 text-xs text-muted-foreground"><span>{data.total} records · Page {page} of {Math.max(1, Math.ceil(data.total / data.limit))}</span><div className="flex gap-2"><Button variant="outline" size="icon" aria-label="Previous page" disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft size={14} /></Button><Button variant="outline" size="icon" aria-label="Next page" disabled={page * data.limit >= data.total} onClick={() => setPage(page + 1)}><ChevronRight size={14} /></Button></div></div>;
}
export function ActionDialog({
  action,
  close,
  children
}) {
  const [reason, setReason] = useState(""),
    [busy, setBusy] = useState(false);
  useEffect(() => {
    setReason("");
  }, [action?.title]);
  const submit = async event => {
    event.preventDefault();
    setBusy(true);
    try {
      await action.run(reason);
      toast.success(action.success || "Change recorded");
      close();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setBusy(false);
    }
  };
  return <Dialog open={!!action} onOpenChange={open => !open && !busy && close()}><DialogContent className="max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>{action?.title}</DialogTitle><DialogDescription>{action?.description || "This change will be recorded in the administration audit trail."}</DialogDescription></DialogHeader><form onSubmit={submit} className="space-y-4">{children}<div><Label htmlFor="admin-action-reason">Reason for this action</Label><Textarea id="admin-action-reason" className="mt-2" required minLength={10} maxLength={1000} value={reason} onChange={e => setReason(e.target.value)} placeholder="Explain the operational reason (at least 10 characters)" /></div><p className="text-[11px] text-muted-foreground">Do not include passwords, API keys or private resume content.</p><Button disabled={busy || reason.trim().length < 10} type="submit" className="w-full">{busy ? <Loader2 className="animate-spin" size={14} /> : <ShieldCheck size={14} />}{busy ? "Saving…" : action?.label || "Confirm change"}</Button></form></DialogContent></Dialog>;
}
export function StateBadge({
  value
}) {
  const good = ["healthy", "completed", "passed", "active", "resolved"].includes(value),
    bad = ["failed", "critical", "unavailable", "high", "disabled"].includes(value);
  return <span className={`inline-flex rounded-md border px-2 py-1 text-[10px] font-medium ${good ? "border-teal-500/20 bg-teal-500/5 text-teal-700 dark:text-teal-300" : bad ? "border-rose-500/20 bg-rose-500/5 text-rose-700 dark:text-rose-300" : "bg-muted text-muted-foreground"}`}>{String(value || "unknown").replaceAll("_", " ")}</span>;
}
